import { useEffect, useMemo, useState } from "react";
import {
  useListStockIn,
  useListInventoryCategories,
  useListInventoryVendors,
  useListStockInOrders,
  useGetStockInSummary,
  useDeleteStockIn,
  getListStockInQueryKey,
  getGetStockInSummaryQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, Coffee, X, Package, Trash2, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { stkInId, ordId } from "@/lib/display-id";
import { StockInForm } from "./stock-in-form";
import { DailyPickupForm } from "./daily-pickup-form";
import { ItemCombobox } from "./item-combobox";
import { VendorCombobox } from "./vendor-combobox";
import { ListPagination } from "@/components/common/list-pagination";
import { StockInOrdersTab, OrdersFilterBar, useOrdersFilters } from "./stock-in-orders-tab";
import { StockInOrderDetailSheet } from "./stock-in-order-detail-sheet";
import { BulkStockUpdateDialog } from "./bulk-stock-update-dialog";

// Phase 2.1 polish G — flat sidebar layout instead of nested tabs.
// Left rail lists modes; selecting a mode renders its content on the right.
//
// Modes:
//   Receipts        : vendor → coworking storage (full PO/QC/financial form)
//   Daily Pickups   : minimal quick-form for milk/dairy/perishables from local shops

const RECEIPT_TYPES = new Set([
  "Purchase",
  "Quick_Commerce_Purchase",
  "Direct_Purchase",
  "Opening_Stock",
  "Member_Return",
  "Sample",
  "Replacement",
  // Phase 22 — delivery receipts for prepaid split-delivery orders appear in the
  // Receipts list with an Order badge (Plan 06 / cross-ref: Plan 05 server STOCK_IN_RECEIPT_TYPES).
  "Order_Delivery",
]);

interface Props {
  openCreate?: boolean;
  setOpenCreate?: (open: boolean) => void;
}

type StockInRow = {
  id: number;
  transactionDate: string;
  createdAt?: string | null;
  transactionType: string;
  vendorId: number | null;
  grandTotal: number | string | null;
  finalPaidAmount?: number | string | null;
  vendorNameFreeText?: string | null;
  lineItemCount?: number | null;
  /** Derived: "Stocked" when the receipt is fully complete, else "Draft". */
  stockStatus?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  invoicePdfUrl?: string | null;
  /** Phase 22 — FK to stock_in_orders.id for Order_Delivery receipts. */
  stockInOrderId?: number | null;
  /** Parent PO's order_id text (e.g. LEGACY-41 or ORD-260530-001). Null when not linked. */
  parentOrderRef?: string | null;
};

/**
 * Renders a parent-order reference. LEGACY-prefixed orders get a small
 * "Legacy" badge and the prefix is stripped from the displayed id, so the
 * Receipts table doesn't shout "LEGACY-LEGACY-LEGACY" down the column.
 */
function ParentOrderCell({ ref, onClick }: { ref: string; onClick: () => void }) {
  const isLegacy = ref.startsWith("LEGACY-");
  const displayId = isLegacy ? ref.replace(/^LEGACY-/, "ORD-") : ref;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="inline-flex items-center gap-1.5 rounded bg-blue-50 px-2 py-0.5 font-mono text-xs font-medium text-blue-700 hover:bg-blue-100"
      title="View parent order"
    >
      <Package className="h-3 w-3" />
      <span>{displayId}</span>
      {isLegacy && (
        <span className="rounded bg-amber-100 px-1 py-px text-[10px] font-semibold uppercase tracking-wide text-amber-700">
          Legacy
        </span>
      )}
    </button>
  );
}

type Mode = "receipts" | "daily" | "orders";

const DERIVED_STATUSES = ["pending", "partial", "completed"] as const;
type DerivedStatus = (typeof DERIVED_STATUSES)[number];

const fmtINR = (n: number) => `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const isFilled = (v: unknown): boolean => v != null && String(v).trim() !== "";

// Invoice status — MUST mirror the server SQL in operations-stock-in.ts (invoiceStatus filter):
//   completed = invoice PDF uploaded; partial = number/date entered but no PDF; pending = nothing.
const invoiceStatusOf = (r: StockInRow): DerivedStatus => {
  if (isFilled(r.invoicePdfUrl)) return "completed";
  if (isFilled(r.invoiceNumber) || r.invoiceDate != null) return "partial";
  return "pending";
};

// R1.5 (migration 0047): paymentDetailStatusOf retired — payment lives on the parent PO.

const STATUS_PILL: Record<DerivedStatus, string> = {
  completed: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-700",
  pending: "bg-muted text-muted-foreground",
};
function StatusPill({ status }: { status: DerivedStatus }) {
  return (
    <span className={cn("inline-flex rounded px-1.5 py-0.5 text-xs font-medium capitalize", STATUS_PILL[status])}>
      {status}
    </span>
  );
}

// Parse a native date-input value ("YYYY-MM-DD") as a LOCAL calendar date
// (new Date("YYYY-MM-DD") would parse as UTC and shift the day in IST).
const parseLocalDate = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
};

// "YYYY-MM-DD" filter input -> ISO datetime bound. The upper bound is end-of-day
// so the selected day is inclusive on the server.
const dayStartISO = (s: string): string => {
  const d = parseLocalDate(s);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};
const dayEndISO = (s: string): string => {
  const d = parseLocalDate(s);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};
// Local "YYYY-MM-DD" helpers for the default Transaction Date range.
// 2026-05-31: operator changed the default from MTD to a 30-day rolling
// window so legacy / older receipts surface without clearing filters.
const ymdLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const thirtyDaysAgoLocal = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return ymdLocal(d);
};
const todayLocal = (): string => ymdLocal(new Date());

const startOfTodayISO = (): string => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};
const endOfTodayISO = (): string => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

const MODES: { id: Mode; label: string; icon: typeof ShoppingCart }[] = [
  { id: "receipts", label: "Receipts", icon: ShoppingCart },
  { id: "daily", label: "Daily Pickups", icon: Coffee },
  { id: "orders", label: "Orders", icon: Package },
];

const PAYMENT_METHODS = ["Cash", "UPI", "Bank_Transfer", "Debit_Card", "Credit_Card", "Wallet", "Cheque"];

export function StockInTab({ openCreate, setOpenCreate }: Props = {}) {
  const [mode, setMode] = useState<Mode>("receipts");
  const [editingId, setEditingId] = useState<number | null>(null);
  // Bulk Stock Update dialog (CSV export + import for on-hand reconciliation).
  // Lives on the Stock-In tab because it's a stock operation; not gated by mode.
  const [bulkStockOpen, setBulkStockOpen] = useState(false);
  // Phase 22 — state for opening an Order detail drawer from an Order_Delivery badge
  // in the Receipts table (without switching to the Orders mode).
  const [receiptOrderDetailId, setReceiptOrderDetailId] = useState<number | null>(null);
  // Date-range filters (server-side): transaction date and record creation date.
  // Transaction date defaults to a 30-day rolling window ending today.
  const [txnFrom, setTxnFrom] = useState(thirtyDaysAgoLocal);
  const [txnTo, setTxnTo] = useState(todayLocal);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [itemId, setItemId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [vendorId, setVendorId] = useState<number | null>(null);
  // R1.5 (migration 0047): paymentMethod / paymentDetailStatus filters retired.
  const [invoiceStatus, setInvoiceStatus] = useState<DerivedStatus | null>(null);
  const [stkIdInput, setStkIdInput] = useState("");
  const [stockStatusFilter, setStockStatusFilter] = useState<"Stocked" | "Draft" | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // STK-IN ID filter accepts "17" or "STK-IN-017" — keep only the digits.
  const stkIdDigits = stkIdInput.replace(/\D/g, "");
  const anyFilter = !!(txnFrom || txnTo || createdFrom || createdTo || itemId != null || categoryId != null || vendorId != null || invoiceStatus || stkIdDigits || stockStatusFilter);
  const clearFilters = () => {
    setTxnFrom("");
    setTxnTo("");
    setCreatedFrom("");
    setCreatedTo("");
    setItemId(null);
    setCategoryId(null);
    setVendorId(null);
    setInvoiceStatus(null);
    setStkIdInput("");
    setStockStatusFilter(null);
  };

  const { data: categories = [] } = useListInventoryCategories();
  const { data: vendors = [] } = useListInventoryVendors();
  const vendorName = useMemo(() => {
    const m = new Map<number, string>();
    for (const v of vendors) m.set(v.id, v.vendorName);
    return m;
  }, [vendors]);

  // Filter inputs -> ISO datetime bounds + item/category/payment-method passed to the server.
  const filterParams = {
    dateFrom: txnFrom ? dayStartISO(txnFrom) : undefined,
    dateTo: txnTo ? dayEndISO(txnTo) : undefined,
    createdFrom: createdFrom ? dayStartISO(createdFrom) : undefined,
    createdTo: createdTo ? dayEndISO(createdTo) : undefined,
    ...(itemId != null ? { itemId } : {}),
    ...(categoryId != null ? { categoryId } : {}),
    ...(vendorId != null ? { vendorId } : {}),
    ...(invoiceStatus ? { invoiceStatus } : {}),
    ...(stkIdDigits ? { id: Number(stkIdDigits) } : {}),
    ...(stockStatusFilter ? { stockStatus: stockStatusFilter } : {}),
  };

  // Reset to first page whenever the active mode, filters, or page size change.
  useEffect(() => {
    setPage(1);
  }, [mode, txnFrom, txnTo, createdFrom, createdTo, itemId, categoryId, vendorId, invoiceStatus, stkIdDigits, stockStatusFilter, pageSize]);

  // One paginated query per group so both sidebar counts stay accurate. The
  // inactive group is fetched at pageSize 1 — we only need its total, not rows.
  const receiptsQuery = useListStockIn({
    group: "receipts",
    page: mode === "receipts" ? page : 1,
    pageSize: mode === "receipts" ? pageSize : 1,
    ...filterParams,
  });
  const dailyQuery = useListStockIn({
    group: "daily",
    page: mode === "daily" ? page : 1,
    pageSize: mode === "daily" ? pageSize : 1,
    ...filterParams,
  });
  // Phase 22 — orders count for the left-rail badge. Fetched at pageSize 1 so we
  // only read the total; the Orders tab owns its own full query.
  const ordersCountQuery = useListStockInOrders({ page: 1, pageSize: 1 });

  const activeQuery = mode === "receipts" ? receiptsQuery : dailyQuery;
  const isLoading = activeQuery.isLoading;
  const rows = (activeQuery.data?.items ?? []) as StockInRow[];
  const total = activeQuery.data?.total ?? 0;
  const counts: Record<Mode, number> = {
    receipts: receiptsQuery.data?.total ?? 0,
    daily: dailyQuery.data?.total ?? 0,
    orders: ordersCountQuery.data?.total ?? 0,
  };

  // KPI summary tiles — driven by the same date range as the Transaction Date filter
  // (defaults to MTD on first load). Server returns current + previous-period sums.
  const summaryFromISO = dayStartISO(txnFrom || thirtyDaysAgoLocal());
  const summaryToISO   = dayEndISO(txnTo   || todayLocal());
  const summaryQuery = useGetStockInSummary({ dateFrom: summaryFromISO, dateTo: summaryToISO });
  const summary = summaryQuery.data;
  // Compute growth label "+12% / -5% / new / —" vs previous period.
  const growth = (curr: number, prev: number): { text: string; dir: "up" | "down" | "flat" } => {
    if (!prev && !curr) return { text: "—", dir: "flat" };
    if (!prev) return { text: "new", dir: "up" };
    const pct = ((curr - prev) / prev) * 100;
    if (Math.abs(pct) < 0.5) return { text: "0%", dir: "flat" };
    return { text: `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`, dir: pct > 0 ? "up" : "down" };
  };
  const growthCls = (dir: "up" | "down" | "flat") =>
    dir === "up" ? "text-green-700" : dir === "down" ? "text-red-700" : "text-muted-foreground";

  // R4 — top-level "Record Delivery" entry point (PO → tick lines → submit).
  // Delete-receipt action: admin-only on the server, but we surface the
  // confirm dialog for everyone — server returns 403 if not admin.
  const [deleteReceiptId, setDeleteReceiptId] = useState<number | null>(null);
  const qcDel = useQueryClient();
  const { toast: toastDel } = useToast();
  const deleteReceiptMut = useDeleteStockIn({
    mutation: {
      onSuccess: () => {
        qcDel.invalidateQueries({ queryKey: getListStockInQueryKey() });
        qcDel.invalidateQueries({ queryKey: getGetStockInSummaryQueryKey() });
        setDeleteReceiptId(null);
        toastDel({ title: "Stock-in deleted · inventory reversed." });
      },
      onError: (err: unknown) => {
        const msg = err && typeof err === "object" && "response" in err
          ? ((err as { response?: { data?: { error?: string } } }).response?.data?.error ?? "Delete failed.")
          : "Delete failed.";
        toastDel({ title: msg, variant: "destructive" });
      },
    },
  });
  // Shared filters for the Orders mode — rendered in the outer filter slot
  // so the Orders UI matches the universal filter row used by Receipts.
  const ordersFilters = useOrdersFilters();

  const sheetOpen = !!openCreate || editingId !== null;

  useEffect(() => {
    if (!sheetOpen) setEditingId(null);
  }, [sheetOpen]);

  function closeSheet() {
    setEditingId(null);
    setOpenCreate?.(false);
  }

  // Column definitions for the External Receipts table — @tanstack/react-table
  // headless instance, rendered with shadcn primitives below. Kept in this
  // scope so cell callbacks can reach setEditingId / setDeleteReceiptId /
  // setReceiptOrderDetailId and the vendorName map without prop drilling.
  // `meta.cellClassName` carries per-cell Tailwind so the rendering loop
  // stays generic.
  type ReceiptColumn = ColumnDef<StockInRow, unknown> & {
    meta?: { cellClassName?: string; headClassName?: string };
  };
  const receiptColumns = useMemo<ReceiptColumn[]>(() => [
    {
      id: "stkIn",
      header: "STK-IN",
      cell: ({ row }) => <>{stkInId(row.original.id)}</>,
      meta: { cellClassName: "font-mono text-xs" },
    },
    {
      id: "txnDate",
      header: "Txn Date",
      cell: ({ row }) => <>{new Date(row.original.transactionDate).toLocaleDateString()}</>,
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "vendor",
      header: "Vendor / Type",
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">
              {(r.vendorId != null ? vendorName.get(r.vendorId) : null) ?? r.vendorNameFreeText ?? "—"}
            </span>
            <span className="text-xs text-muted-foreground">
              {r.transactionType.replace(/_/g, " ")} · {r.lineItemCount ?? 0} {(r.lineItemCount ?? 0) === 1 ? "item" : "items"}
            </span>
          </div>
        );
      },
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "po",
      header: "PO",
      cell: ({ row }) => {
        const r = row.original;
        if (r.stockInOrderId == null || !r.parentOrderRef) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        return (
          <ParentOrderCell
            ref={r.parentOrderRef}
            onClick={() => setReceiptOrderDetailId(r.stockInOrderId!)}
          />
        );
      },
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "grandTotal",
      header: "Grand Total",
      cell: ({ row }) => <>{fmtINR(Number(row.original.grandTotal ?? 0))}</>,
      meta: { cellClassName: "font-mono text-sm tabular-nums" },
    },
    {
      id: "invoice",
      header: "Invoice",
      cell: ({ row }) => <StatusPill status={invoiceStatusOf(row.original)} />,
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <span className={cn(
          "inline-flex rounded px-1.5 py-0.5 text-xs font-medium",
          row.original.stockStatus === "Stocked" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700",
        )}>
          {row.original.stockStatus === "Stocked" ? "Stocked" : "Draft"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const isStocked = row.original.stockStatus === "Stocked";
        return (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            onClick={(e) => { e.stopPropagation(); setDeleteReceiptId(row.original.id); }}
            disabled={isStocked}
            title={isStocked
              ? "Stocked receipts are locked. Only Draft receipts can be deleted."
              : "Delete receipt (admin only) — reverses stock side-effects"}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        );
      },
      meta: { cellClassName: "text-right", headClassName: "w-[44px]" },
    },
  ], [vendorName]);

  // useMemo'd row-model factory — see items-tab.tsx for the root-cause
  // writeup. Without this, useReactTable receives a fresh function ref
  // on every render and re-builds the row model in a loop.
  const coreRowModelFactory = useMemo(() => getCoreRowModel(), []);
  const receiptsTable = useReactTable({
    data: rows as StockInRow[],
    columns: receiptColumns,
    getCoreRowModel: coreRowModelFactory,
  });

  // Daily Pickups — same headless pattern, much smaller column set.
  type DailyColumn = ColumnDef<StockInRow, unknown> & {
    meta?: { cellClassName?: string };
  };
  const dailyColumns = useMemo<DailyColumn[]>(() => [
    {
      id: "stkIn",
      header: "STK-IN",
      cell: ({ row }) => <>{stkInId(row.original.id)}</>,
      meta: { cellClassName: "font-mono text-xs" },
    },
    {
      id: "date",
      header: "Date",
      cell: ({ row }) => <>{new Date(row.original.transactionDate).toLocaleDateString()}</>,
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "shop",
      header: "Shop",
      cell: ({ row }) => <>{row.original.vendorNameFreeText ?? "—"}</>,
      meta: { cellClassName: "text-sm" },
    },
    {
      id: "amount",
      header: "Amount",
      cell: ({ row }) => <>{fmtINR(Number(row.original.finalPaidAmount ?? row.original.grandTotal ?? 0))}</>,
      meta: { cellClassName: "font-mono text-sm tabular-nums" },
    },
  ], []);

  const dailyTable = useReactTable({
    data: rows as StockInRow[],
    columns: dailyColumns,
    getCoreRowModel: coreRowModelFactory,
  });

  return (
    // App-shell for POINTER-FINE devices only (mouse / trackpad
    // desktops + laptops). Touch tablets fall through to default
    // block flow so the whole page scrolls naturally.
    <div className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col">
      {/* Upper strip — KPI tiles + filter bar. Natural height on
          touch; shrink-0 inside the desktop flex column. */}
      <div className="pointer-fine:shrink-0">
      {/* KPI summary tiles — period = Transaction Date filter (defaults to MTD).
          Growth % is current vs previous same-length period.
          Receipts-centric metrics — Orders mode owns its own card so we skip
          the tiles there. */}
      {mode !== "orders" && (
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {(() => {
          const r = summary?.receipts        ?? { current: 0, previous: 0 };
          const p = summary?.pendingReceipts ?? { current: 0, previous: 0 };
          const e = summary?.expense         ?? { current: 0, previous: 0 };
          const h = summary?.expensePerHead  ?? { current: 0, headCount: 0 };
          const gr = growth(r.current, r.previous);
          const gp = growth(p.current, p.previous);
          const ge = growth(e.current, e.previous);
          return (
            <>
              <div className="rounded-md border bg-card px-3 py-2">
                <p className="text-xs text-muted-foreground">Receipts</p>
                <p className="text-base font-semibold tabular-nums">
                  {fmtINR(r.current)}{" "}
                  <span className={cn("text-xs font-medium", growthCls(gr.dir))}>{gr.text}</span>
                </p>
                <p className="text-[11px] text-muted-foreground">Stocked · vs prev period</p>
              </div>
              <div className="rounded-md border bg-card px-3 py-2">
                <p className="text-xs text-muted-foreground">Pending Receipts</p>
                <p className="text-base font-semibold tabular-nums">
                  {fmtINR(p.current)}{" "}
                  <span className={cn("text-xs font-medium", growthCls(gp.dir))}>{gp.text}</span>
                </p>
                <p className="text-[11px] text-muted-foreground">Draft · vs prev period</p>
              </div>
              <div className="rounded-md border bg-card px-3 py-2">
                <p className="text-xs text-muted-foreground">Expense</p>
                <p className="text-base font-semibold tabular-nums">
                  {fmtINR(e.current)}{" "}
                  <span className={cn("text-xs font-medium", growthCls(ge.dir))}>{ge.text}</span>
                </p>
                <p className="text-[11px] text-muted-foreground">Stocked · excl. Pay-Per-Use</p>
              </div>
              <div className="rounded-md border bg-card px-3 py-2">
                <p className="text-xs text-muted-foreground">Expense per head</p>
                <p className="text-base font-semibold tabular-nums">{fmtINR(h.current)}</p>
                <p className="text-[11px] text-muted-foreground">{h.headCount} active member{h.headCount === 1 ? "" : "s"}</p>
              </div>
            </>
          );
        })()}
      </div>
      )}

      {/* Date-range filters: transaction date + record creation date.
          Receipts/Daily Pickups only — Orders mode has its own filter bar
          inside StockInOrdersTab so we don't show two duplicate rows. */}
      {mode !== "orders" && (
      <div className="mb-4 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-md border bg-card px-3 py-2.5">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Transaction date</p>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="Transaction date from"
              value={txnFrom}
              max={txnTo || undefined}
              onChange={(e) => setTxnFrom(e.target.value)}
              className="h-8 w-[150px]"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <Input
              type="date"
              aria-label="Transaction date to"
              value={txnTo}
              min={txnFrom || undefined}
              onChange={(e) => setTxnTo(e.target.value)}
              className="h-8 w-[150px]"
            />
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Created date</p>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="Created date from"
              value={createdFrom}
              max={createdTo || undefined}
              onChange={(e) => setCreatedFrom(e.target.value)}
              className="h-8 w-[150px]"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <Input
              type="date"
              aria-label="Created date to"
              value={createdTo}
              min={createdFrom || undefined}
              onChange={(e) => setCreatedTo(e.target.value)}
              className="h-8 w-[150px]"
            />
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Item</p>
          <div className="flex items-center gap-1">
            <ItemCombobox value={itemId} onChange={(id) => setItemId(id)} showTier={false} placeholder="All items" className="h-8 text-sm w-[220px] font-normal" />
            {itemId != null && (
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Clear item" onClick={() => setItemId(null)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Category</p>
          <Select
            value={categoryId != null ? String(categoryId) : "all"}
            onValueChange={(v) => setCategoryId(v === "all" ? null : Number(v))}
          >
            <SelectTrigger className="h-8 w-[190px]"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Vendor</p>
          <div className="flex items-center gap-1">
            <VendorCombobox value={vendorId} onChange={(id) => setVendorId(id)} className="h-8 text-sm w-[220px]" />
            {vendorId != null && (
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Clear vendor" onClick={() => setVendorId(null)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {/* R1.5 (migration 0047): Payment method + Payment details filters retired
            — payment lives only on the parent PO now. Filter on the Orders tab. */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Invoice status</p>
          <Select
            value={invoiceStatus ?? "all"}
            onValueChange={(v) => setInvoiceStatus(v === "all" ? null : (v as DerivedStatus))}
          >
            <SelectTrigger className="h-8 w-[150px]"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {DERIVED_STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Status</p>
          <Select
            value={stockStatusFilter ?? "all"}
            onValueChange={(v) => setStockStatusFilter(v === "all" ? null : (v as "Stocked" | "Draft"))}
          >
            <SelectTrigger className="h-8 w-[140px]"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Stocked">Stocked</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">STK-IN ID</p>
          <div className="flex items-center gap-1">
            <Input
              value={stkIdInput}
              onChange={(e) => setStkIdInput(e.target.value)}
              placeholder="e.g. 17"
              inputMode="numeric"
              className="h-8 w-[120px]"
            />
            {stkIdInput && (
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Clear STK-IN ID" onClick={() => setStkIdInput("")}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {anyFilter && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-muted-foreground">
            <X className="mr-1 h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>
      )}

      {/* Orders mode uses its own filter bar in the same outer slot so the
          page-level filter UI is consistent across modes. */}
      {mode === "orders" && <OrdersFilterBar filters={ordersFilters} />}
      </div>

      <AlertDialog open={deleteReceiptId !== null} onOpenChange={(o) => { if (!o) setDeleteReceiptId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this stock-in receipt?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently removes the receipt and <span className="font-medium">reverses the stock side-effects</span> —
              each line item's <code>current_stock</code> is decremented by the qty received.
              If linked to a PO, the order's delivery status is recomputed.
              Admin only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteReceiptId != null && deleteReceiptMut.mutate({ id: deleteReceiptId })}
              disabled={deleteReceiptMut.isPending}
            >
              {deleteReceiptMut.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modes layout — flex column on mobile/tablet, row on lg+.
          flex-1 min-h-0 only kicks in on pointer-fine so the Card
          can stretch to fill the desktop viewport. On touch the
          layout has no min-h-0 / flex-1 so the Card sizes to its
          content and the page scroll handles overflow. */}
      <div className="flex flex-col lg:flex-row gap-8 pointer-fine:flex-1 pointer-fine:min-h-0">
        <aside className="shrink-0 space-y-2 lg:w-[220px]">
          {MODES.map((m) => {
            const active = mode === m.id;
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id)}
                className={cn(
                  "w-full text-left rounded-md px-3 h-11 transition-colors flex items-center justify-between gap-3",
                  active ? "bg-primary text-primary-foreground" : "hover:bg-muted/60",
                )}
                aria-current={active ? "page" : undefined}
              >
                <span className="flex items-center gap-3 min-w-0">
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="text-sm font-medium truncate">{m.label}</span>
                </span>
                <span className={cn("text-xs tabular-nums", active ? "text-primary-foreground/80" : "text-muted-foreground")}>{counts[m.id]}</span>
              </button>
            );
          })}
        </aside>

        <main className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col">
          {mode === "orders" ? (
            <StockInOrdersTab filters={ordersFilters} />
          ) : mode === "receipts" ? (
            <Card className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col">
              <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 pointer-fine:shrink-0">
                <div>
                  <CardTitle>External Receipts</CardTitle>
                  <p className="text-sm text-muted-foreground">Vendor → coworking storage. Logged with PO / invoice / quality check.</p>
                </div>
                {/* Bulk Stock Update — separate flow from the per-row CTA so
                    the operator can reconcile on-hand counts in one shot
                    after a stock take, without creating new receipts. */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkStockOpen(true)}
                  data-testid="button-open-bulk-stock-update"
                  className="shrink-0"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Bulk Stock Update
                </Button>
              </CardHeader>
              <CardContent className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:overflow-y-auto">
                {isLoading ? (
                  <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : rows.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    {anyFilter
                      ? "No receipts match the selected filters."
                      : "No external receipts yet. Click Add Stock In to record a vendor delivery."}
                  </div>
                ) : (
                  // On pointer-fine, CardContent is the scroll container
                  // and thead pins at top:0 of THAT. On touch, CardContent
                  // has no internal overflow → thead just renders inline
                  // with the rows and the whole page scrolls.
                  <table className="w-full caption-bottom text-sm">
                    <TableHeader className="pointer-fine:sticky pointer-fine:top-0 pointer-fine:z-10 pointer-fine:bg-background pointer-fine:[&_th]:bg-background">
                      {receiptsTable.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((header) => {
                            const meta = header.column.columnDef.meta as { headClassName?: string } | undefined;
                            return (
                              <TableHead key={header.id} className={meta?.headClassName}>
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </TableHead>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {receiptsTable.getRowModel().rows.map((row) => {
                        // Stocked receipts are locked for edit/delete (server 409s a save),
                        // but the row still opens the sheet so the operator can VIEW line
                        // items + totals + invoice + charges. The plain StockInForm loads
                        // all data; any attempted save on a Stocked row is rejected
                        // server-side, so this is safe.
                        const isStocked = row.original.stockStatus === "Stocked";
                        return (
                          <TableRow
                            key={row.id}
                            onClick={() => setEditingId(row.original.id)}
                            className="cursor-pointer"
                            title={isStocked ? "Stocked receipts open in view mode — save is locked." : undefined}
                          >
                            {row.getVisibleCells().map((cell) => {
                              const meta = cell.column.columnDef.meta as { cellClassName?: string } | undefined;
                              return (
                                <TableCell key={cell.id} className={meta?.cellClassName}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </table>
                )}
                {!isLoading && total > 0 && (
                  <ListPagination
                    total={total}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    isLoading={isLoading}
                    noun={["receipt", "receipts"]}
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:flex pointer-fine:flex-col">
              <CardHeader className="pointer-fine:shrink-0">
                <CardTitle>Daily Pickups</CardTitle>
                <p className="text-sm text-muted-foreground">Quick log for milk / dairy / daily perishables from local shops. No PO or QC needed.</p>
              </CardHeader>
              <CardContent className="pointer-fine:flex-1 pointer-fine:min-h-0 pointer-fine:overflow-y-auto">
                {isLoading ? (
                  <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
                ) : rows.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    {anyFilter
                      ? "No daily pickups match the selected filters."
                      : "No daily pickups yet. Click Add Stock In to log today's milk/dairy."}
                  </div>
                ) : (
                  <table className="w-full caption-bottom text-sm">
                    <TableHeader className="pointer-fine:sticky pointer-fine:top-0 pointer-fine:z-10 pointer-fine:bg-background pointer-fine:[&_th]:bg-background">
                      {dailyTable.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((header) => (
                            <TableHead key={header.id}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {dailyTable.getRowModel().rows.map((row) => (
                        <TableRow
                          key={row.id}
                          onClick={() => setEditingId(row.original.id)}
                          className="cursor-pointer"
                        >
                          {row.getVisibleCells().map((cell) => {
                            const meta = cell.column.columnDef.meta as { cellClassName?: string } | undefined;
                            return (
                              <TableCell key={cell.id} className={meta?.cellClassName}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </table>
                )}
                {!isLoading && total > 0 && (
                  <ListPagination
                    total={total}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    isLoading={isLoading}
                    noun={["pickup", "pickups"]}
                  />
                )}
              </CardContent>
            </Card>
          )}
        </main>
      </div>

      <Sheet open={sheetOpen} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent
          side="right"
          className={mode === "daily" && !editingId ? "w-full sm:max-w-md overflow-y-auto" : "w-full sm:max-w-5xl overflow-y-auto"}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>
              {editingId ? "Edit Stock In" : mode === "daily" ? "Add Daily Pickup" : "Add Stock In"}
            </SheetTitle>
          </SheetHeader>
          {mode === "daily" && !editingId ? (
            <DailyPickupForm onSaved={closeSheet} onDiscard={closeSheet} />
          ) : (
            <StockInForm
              mode={editingId ? "edit" : "create"}
              stockInId={editingId}
              onSaved={closeSheet}
              onDiscard={closeSheet}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Phase 22 — Order detail drawer opened via the Order badge on Order_Delivery rows */}
      {receiptOrderDetailId != null && (
        <StockInOrderDetailSheet
          orderId={receiptOrderDetailId}
          onClose={() => setReceiptOrderDetailId(null)}
        />
      )}

      <BulkStockUpdateDialog open={bulkStockOpen} onOpenChange={setBulkStockOpen} />
    </div>
  );
}
